/**
 * Import Monitor — Collaboration Holds tab.
 *
 * When a proposed schedule date/qty change is accepted in the portal, the
 * slot carries the agreed value while the ERP still holds the old one (the
 * writeback is queued but not applied yet). Those slots are frozen against
 * ERP schedule imports so a push can't silently undo the agreement. This
 * tab lists every held slot: the agreed value, what the ERP last tried to
 * send, and how long the hold has been open. The hold releases itself the
 * moment the ERP sends the agreed value — rows that sit here for long mean
 * the writeback hasn't been applied in the ERP and needs chasing.
 *
 * Customer-side only, and only rendered when the tenant runs schedule
 * collaboration (the parent gates on the feature flag).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  importsAPI,
  type CollabHoldsResponse,
} from '../../lib/api/imports';

interface ImportCollabHoldsTabProps {
  customerId: number;
}

const PAGE_SIZE = 25;

export default function ImportCollabHoldsTab({ customerId }: ImportCollabHoldsTabProps) {
  const [data, setData] = useState<CollabHoldsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await importsAPI.getCollabHolds(customerId, {
        search: search || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setData(resp);
    } catch (err: unknown) {
      console.error('Collaboration holds load failed:', err);
      setError('Failed to load collaboration holds');
      toast.error('Failed to load collaboration holds');
    } finally {
      setLoading(false);
    }
  }, [customerId, search, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Slots on hold</p>
            <p className="text-2xl font-semibold tabular-nums">{data.summary.total.toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">ERP still sending old value</p>
            <p className="text-2xl font-semibold tabular-nums text-amber-600">{data.summary.erp_attempted.toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Not seen from ERP yet</p>
            <p className="text-2xl font-semibold tabular-nums">{data.summary.never_seen_from_erp.toLocaleString()}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            className="pl-8 pr-3 py-2 text-sm border rounded-md w-64"
            placeholder="PO or part…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(1); }
            }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-red-600">{error}</div>
          ) : !data || data.results.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No schedules are holding an accepted change — the ERP is fully in sync.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-left">
                    <th className="py-2 px-3 font-medium">PO</th>
                    <th className="py-2 px-3 font-medium">Line</th>
                    <th className="py-2 px-3 font-medium">Sched</th>
                    <th className="py-2 px-3 font-medium">Part</th>
                    <th className="py-2 px-3 font-medium">Agreed Date</th>
                    <th className="py-2 px-3 font-medium text-right">Agreed Qty</th>
                    <th className="py-2 px-3 font-medium">ERP Last Sent</th>
                    <th className="py-2 px-3 font-medium">Held Since</th>
                    <th className="py-2 px-3 font-medium">Accepted By</th>
                    <th className="py-2 px-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((r) => (
                    <tr key={r.schedule_id} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="py-2 px-3 font-medium">{r.po_number}</td>
                      <td className="py-2 px-3">{r.line_number ?? '—'}</td>
                      <td className="py-2 px-3">{r.schedule_line_number ?? '—'}</td>
                      <td className="py-2 px-3">{r.part_number ?? '—'}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{r.agreed_date ?? '—'}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{r.agreed_qty ?? '—'}</td>
                      <td className="py-2 px-3 whitespace-nowrap text-xs">
                        {r.erp_last_seen_at ? (
                          <span title={new Date(r.erp_last_seen_at).toLocaleString()}>
                            {r.erp_last_seen_date ?? r.erp_date ?? '—'}
                            {r.erp_last_seen_qty != null ? ` × ${r.erp_last_seen_qty}` : ''}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">not seen yet</span>
                        )}
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap">
                        {r.held_since ? new Date(r.held_since).toLocaleDateString() : '—'}
                        {r.held_days != null && r.held_days > 0 ? (
                          <span className={`ml-1 text-xs ${r.held_days > 7 ? 'text-red-600' : 'text-muted-foreground'}`}>
                            ({r.held_days}d)
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 px-3">{r.accepted_by ?? '—'}</td>
                      <td className="py-2 px-3">
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                          <ShieldCheck className="h-3 w-3 mr-1" /> Protected
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {data.page} of {totalPages} · {data.count.toLocaleString()} holds
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
